import { useState, useEffect, useCallback, useMemo } from 'react';
import { employeeService } from '../services/employeeService';

export function useEmployees(apiFilterPayload, loadingEnums) {
  const [employees, setEmployees] = useState([]);         // Raw data from server
  const [displayList, setDisplayList] = useState([]);     // Data after local search
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");

  // 1. Fetch data from server whenever the Applied Payload changes
  const fetchEmployees = useCallback(async () => {
    if (loadingEnums) return;

    setLoading(true);
    try {
      // console.log("Fetching employees with payload:", apiFilterPayload); // Debug log to verify payload structure
      const data = await employeeService.fetchFiltered(apiFilterPayload);
      setEmployees(data);
      setDisplayList(data); // Initialize display list with raw data
    } catch (error) {
      console.error("Failed to fetch employees", error);
    } finally {
      setLoading(false);
    }
  }, [apiFilterPayload, loadingEnums]);

  // Trigger fetch on payload change
  useEffect(() => {
    fetchEmployees();
  }, [fetchEmployees]);

  // 2. Handle Local Search (Search Bar)
  // This updates the displayList without a new network request
  const handleSearch = useCallback((value) => {
    setSearchTerm(value);
    const lower = value.toLowerCase();

    if (!value) {
      setDisplayList(employees);
      return;
    }

    const filtered = employees.filter((emp) => {
      const fullNameMatch = emp.fullName?.toLowerCase().includes(lower);
      const idMatch = emp.employeeId?.toString().toLowerCase().includes(lower);
      return fullNameMatch || idMatch;
    });

    setDisplayList(filtered);
  }, [employees]);

  // 3. Derived State: Unapproved List
  const unapprovedEmployees = useMemo(() => {
    return employees
      .filter(emp => emp.employeeInformation?.employeeInformationStatus !== "Approved")
      .sort((a, b) => a.fullName.localeCompare(b.fullName));
  }, [employees]);

  return {
    employees: displayList,       // This is what the Table renders
    rawEmployees: employees,      // Original list if needed
    unapprovedEmployees,
    loading,
    handleSearch,                 // Pass this to SearchBar component
    refresh: fetchEmployees       // Call this after bulk actions
  };
}